import {BitmapText, Graphics} from "pixi.js";
import {DrawFrmThing} from "./DrawFrmThing";
import {distance, EPS, FrmShapePoint} from "./FrmShapePoint";
import {FrmShapeLine} from "./FrmShapeLine";
import {assignDimColumns, DimensionKind, DimensionSide, FrmShapeDimension} from "./FrmShapeDimension";
import {FrmShapeConstraint} from "./FrmShapeConstraint";
import {DrawFrmApp} from "./DrawFrmApp";
import * as kiwi from '@lume/kiwi'
import {inches} from "../const";
import {FrmVar} from "./FrmVar";
import {EventEmitter, Subscription} from "nate-react-api-helpers";

// the shortest a flex line may solve to. Shared with MdFrameSpec so profile defaults
// can leave room for it.
export const MIN_FLEX_LENGTH = inches / 16;


// distance (in shape units) the label sits off the line, outside the shape
const LABEL_OFFSET = 6;
const LABEL_ENABLE = false;
const DEBUG = false;

// dimension rendering. per-dimension line styling (color/width) is set by
// FrmShapeDimension.draw().
const DIM_ENABLE = true;

// FrmShape is a line drawing with dimensions, constraints and inter-shape relationships
export class FrmShape {

    dimensions: FrmShapeDimension[] = [];
    lines: FrmShapeLine[] = [];
    points: FrmShapePoint[] = [];
    constraints: FrmShapeConstraint[] = [];

    queue: (() => void)[] = [];
    priorityQueue: (() => void)[] = [];

    lastPoint: FrmShapePoint | null = null;
    thing: DrawFrmThing;
    isDone: boolean = false;

    // when set, x coordinates/deltas are negated as points are added,
    // mirroring the shape across the y-axis. see invertX().
    invertXEnabled: boolean = false;

    // when set, y coordinates/deltas are negated as points are added,
    // mirroring the shape across the x-axis. see invertY().
    invertYEnabled: boolean = false;

    strokeWidth: number = 2;
    strokeColor: number = 0x000000;

    defaultCornerRadius: number = 2;

    app: DrawFrmApp;
    horizontalDimensionSide: "left" | "right" = "right";

    name: string;
    subscriptions: Subscription[] = [];

    dimensionMode: DimensionKind | false = "extended";

    // debug markers: a red dot + label pinned to a point. drawn last (on top of
    // everything) and re-rendered each draw() so they follow point movement and
    // survive refresh/scale. see flagForDebug().
    private debugFlags: { point: FrmShapePoint, label: string, text: BitmapText | null }[] = [];
    private debugFlagGraphic: Graphics | null = null;

    constructor(name: string, opts: {
        dimensionMode: EventEmitter<DimensionKind | false>
        thing: DrawFrmThing
        app: DrawFrmApp
    }) {
        this.name = name;
        this.thing = opts.thing;
        this.app = opts.app;
        this.dimensionMode = opts.dimensionMode.lastValue ?? "extended";

        this.subscriptions.push(this.app.refresh.subscribe(() => this.refresh()));

        this.subscriptions.push(opts.dimensionMode.subscribe(mode => {
            this.dimensionMode = mode;
            this.draw();
        }));


        // tie this shape's lifecycle to the thing so its dimensions' FrmVar
        // subscriptions are cancelled when the thing is destroyed.
        this.thing.track(this);
    }

    // cancel dimension subscriptions. called via DrawFrmThing.destroy().
    destroy() {
        for (const d of this.dimensions) d.destroy();
        this.dimensions = [];

        this.subscriptions.forEach(s => s.cancel());
        this.subscriptions = [];
    }

    // mirror the shape across the y-axis: x coordinates (in start) and x
    // deltas (in lineTo) are negated as points are added. call before start().
    invertX() {
        this.invertXEnabled = true;
    }

    // mirror the shape across the x-axis: y coordinates (in start) and y
    // deltas (in lineTo) are negated as points are added. call before start().
    invertY() {
        this.invertYEnabled = true;
    }

    // starting point for point sequence to build the shape
    start(x: number, y: number): FrmShapePoint {
        if (this.invertXEnabled) x = -x;
        if (this.invertYEnabled) y = -y;

        const point = new FrmShapePoint(this, x, y);
        this.points.push(point);
        this.lastPoint = point;
        return point;
    }

    lineTo(dX: number, dY: number): FrmShapeLine {
        if(!this.lastPoint) {
            throw new Error("FrmShape.start() must be called before lineTo()");
        }

        if (this.invertXEnabled) dX = -dX;
        if (this.invertYEnabled) dY = -dY;

        return this.lineToPoint(this.lastPoint.x + dX, this.lastPoint.y + dY);
    }

    // append a point and create a line using the previous point and the current point
    lineToPoint(x: number, y: number): FrmShapeLine {
        if (!this.lastPoint) {
            throw new Error("FrmShape.start() must be called before lineTo()");
        }

        const point = new FrmShapePoint(this, x, y);
        this.points.push(point);
        const line = new FrmShapeLine(this, this.lastPoint, point);
        this.lines.push(line);

        line.setCornerRadius(this.defaultCornerRadius);
        line.debugLabelText = String.fromCharCode(97 + (this.lines.length % 26));

        this.lastPoint = point;
        return line;
    }

    // while set, collects the FrmVar behind every dimension any shape adds.
    // see recordDimensionVars().
    private static dimensionRecord: Set<FrmVar> | null = null;

    // run fn and collect the FrmVar behind every dimension added (in any shape)
    // while it runs. Lets a caller build a drawing off-screen and ask which vars
    // it actually references.
    static recordDimensionVars(fn: () => void): Set<FrmVar> {
        const prev = FrmShape.dimensionRecord;
        const record = new Set<FrmVar>();
        FrmShape.dimensionRecord = record;
        try {
            fn();
        } finally {
            FrmShape.dimensionRecord = prev;
        }
        return record;
    }

    // add a dimension between two points.
    // by default vertical dimensions render on the right and horizontal on the bottom;
    // pass side to override (left/right for vertical, top/bottom for horizontal).
    addDimension(pointA: FrmShapePoint, pointB: FrmShapePoint, frmVar: FrmVar, kind: DimensionKind = "standard", side?: DimensionSide): FrmShapeDimension {
        FrmShape.dimensionRecord?.add(frmVar);
        if(!side) {
            side = this.horizontalDimensionSide;
        }

        const dim = new FrmShapeDimension(this, pointA, pointB, frmVar, kind, side);
        this.dimensions.push(dim);
        return dim;
    }

    // hide (don't render) every dimension backed by one of `vars`, keeping its length
    // constraint. Used by a clamshell mullion's pen-up half to drop its now-redundant
    // face / jamb-depth dimensions. Call before done().
    hideDimensionsForVars(vars: FrmVar[]) {
        for(const d of this.dimensions) {
            if(vars.indexOf(d.frmVar) !== -1) d.hidden = true;
        }
    }

    // finalize the shape and apply all dimensions and rules
    done() {
        this.isDone = true;

        if(DEBUG) console.log("run priority queue", this);
        this.priorityQueue.forEach(fn => fn());

        const dimsRemain = this.dimensions.filter(d => {
            const line = this.lines.find(l => {
                return (l.pointA === d.pointA && l.pointB === d.pointB) || (l.pointA === d.pointB && l.pointB === d.pointA);
            });
            if(!line) return true;

            this.forceUpdateLength(line, d.frmVar.get());

            this.constraints.forEach(c => {
                if(c.lines.indexOf(line) === -1) return;

                c.lines.forEach(l => {
                    if(l === line) return;
                    this.forceUpdateLength(l, d.frmVar.get());
                })
            });

            return false;
        });

        dimsRemain.forEach(d => {
            if(DEBUG) console.log("dim-setup", d.frmVar.get(), "(" + (d.frmVar.get() / inches) + " inches)")
            this.updateLength(FrmShape.getUpdateLengthId(), d.pointA, d.pointB, d.frmVar.get(), "init-dimension", d);
        });

        this.dimensions.forEach(d => {
            d.enableVarReporting = true;
        });

        this.draw();
    }

    refresh() {
        this.draw();
    }

    private draw(shapeUpdateId?: number) {
        const g = this.thing.lines;
        g.clear();

        if (this.points.length === 0) {
            return;
        }

        g.lineStyle(this.strokeWidth, this.strokeColor, 1);

        const first = this.points[0];
        g.moveTo(first.x, first.y);

        const lines = this.lines;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // pen-up: don't stroke this segment, just move the pen to its end so
            // the next segment starts fresh, leaving a gap (e.g. FrameProfileEXP).
            if (line.penUp) {
                g.moveTo(line.pointB.x, line.pointB.y);
                continue;
            }

            const hasNext = i + 1 < lines.length;
            // don't round into a gap — the corner before a pen-up bridge is drawn sharp.
            const nextIsPenUp = hasNext && lines[i + 1].penUp;
            const r = line.cornerRadius;

            if (hasNext && !nextIsPenUp && r > EPS) {
                // arc around the corner at the end of this line into the next line
                const next = lines[i + 1];
                g.arcTo(line.pointB.x, line.pointB.y, next.pointB.x, next.pointB.y, r);
            } else {
                g.lineTo(line.pointB.x, line.pointB.y);
            }
        }

        this.drawLabels();
        this.drawDimensions(shapeUpdateId);
        this.drawDebugFlags();
    }

    // red dot + label pinned to a point, used to mark a spot while debugging.
    // the marker is rendered last (on top) and persists across refresh/scale.
    flagForDebug(point: FrmShapePoint, label: string) {
        this.debugFlags.push({point, label, text: null});
        if (this.isDone) this.draw();
    }

    private drawDebugFlags() {
        if (this.debugFlags.length === 0) return;

        if (!this.debugFlagGraphic) {
            this.debugFlagGraphic = new Graphics();
            this.thing.graphic.addChild(this.debugFlagGraphic);
        }

        const g = this.debugFlagGraphic;
        g.clear();

        const radius = 3;
        for (const flag of this.debugFlags) {
            g.beginFill(0xff0000, 1);
            g.drawCircle(flag.point.x, flag.point.y, radius);
            g.endFill();

            if (!flag.text) {
                flag.text = new BitmapText(flag.label, {fontName: "Arial", fontSize: 10});
                flag.text.anchor.set(0, 0.5);
                flag.text.tint = 0xff0000;
                this.thing.graphic.addChild(flag.text);
            }

            flag.text.position.set(flag.point.x + radius + 2, flag.point.y);
        }

        // re-add to the end so the markers render on top of lines/dimensions/labels.
        this.thing.graphic.addChild(this.debugFlagGraphic);
        for (const flag of this.debugFlags) {
            if (flag.text) this.thing.graphic.addChild(flag.text);
        }
    }

    // place an a/b/c label next to each line, on the outside of the shape
    private drawLabels() {
        if (!LABEL_ENABLE) return;

        const lines = this.lines;
        if (lines.length === 0) return;

        // centroid of all points — labels go on the opposite side of each line
        let cx = 0;
        let cy = 0;
        for (let i = 0; i < this.points.length; i++) {
            cx += this.points[i].x;
            cy += this.points[i].y;
        }
        cx /= this.points.length;
        cy /= this.points.length;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const label = line.ensureDebugLabel();

            const ax = line.pointA.x;
            const ay = line.pointA.y;
            const bx = line.pointB.x;
            const by = line.pointB.y;

            const mx = (ax + bx) * 0.5;
            const my = (ay + by) * 0.5;

            const dx = bx - ax;
            const dy = by - ay;
            const len = Math.sqrt(dx * dx + dy * dy);

            if (len < EPS) {
                label.position.set(mx, my);
                continue;
            }

            // perpendicular unit normal; flip it so it points away from the centroid
            let nx = -dy / len;
            let ny = dx / len;
            if (nx * (cx - mx) + ny * (cy - my) > 0) {
                nx = -nx;
                ny = -ny;
            }

            label.position.set(mx + nx * LABEL_OFFSET, my + ny * LABEL_OFFSET);
        }
    }

    // render dimensions on the outer sides of the drawing's bounding box.
    // dimensions on the same side are stacked into columns so overlapping ones
    // are pushed further out, matching a typical engineering drawing dimension view.
    private drawDimensions(shapeUpdateId?: number) {
        if (!DIM_ENABLE) return;
        if (this.dimensions.length === 0 || this.points.length === 0) return;

        // dimensions hidden: the dimension lines live in the shared `lines`
        // graphics, already cleared at the top of draw(), so skipping the render
        // below removes them. the persistent labels need explicit hiding.
        if (this.dimensionMode === false) {
            this.dimensions.forEach(d => d.hide());
            return;
        }

        // per-dimension hidden flag (e.g. a clamshell secondary side): hide the label;
        // skipping the render below drops the line. The length constraint still ran in
        // done(), so the geometry is unaffected.
        let visible = this.dimensions.filter(d => {
            if(!d.hidden) return true;
            d.hide();
            return false;
        });
        if(this.dimensionMode === "standard") {
            visible = visible.filter(d => {
                if(d.kind === "standard") return true;

                d.hide();
                return false;
            });
        }

        const g = this.thing.lines;

        // bounding box of the geometry
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (let i = 0; i < this.points.length; i++) {
            const p = this.points[i];
            if (p.x < minX) minX = p.x;
            if (p.x > maxX) maxX = p.x;
            if (p.y < minY) minY = p.y;
            if (p.y > maxY) maxY = p.y;
        }
        const bbox = {minX, minY, maxX, maxY};

        const sides: DimensionSide[] = ["left", "right", "top", "bottom"];
        for (const side of sides) {
            const group = visible.filter(d => d.side === side);
            if (group.length === 0) continue;

            const columns = assignDimColumns(group);
            for (let i = 0; i < group.length; i++) {
                group[i].draw(g, columns[i], bbox, shapeUpdateId);
            }
        }
    }

    forceUpdateLength(line: FrmShapeLine, length: number) {
        if(!this.isDone) {
            this.priorityQueue.push(() => this.forceUpdateLength(line, length))
            return;
        }

        if(DEBUG) console.log("forceUpdateLength", line.debugLabelText, length);

        const dist = distance(line.pointA, line.pointB);
        const add = length - dist;
        if(Math.abs(add) < EPS) return;

        const dx = (line.pointB.x - line.pointA.x) / dist * add;
        const dy = (line.pointB.y - line.pointA.y) / dist * add;

        this.movePoint(line.pointB, dx, dy);
        this.emitChanges("Shape.forceUpdateLength");
    }

    // notify listeners (e.g. FrmRefPoint links) that points have moved.
    // batched: called once at the end of an action rather than per-point.
    private emitChanges(debugName: string) {
        for(let i = 0; i < this.points.length; i++) {
            this.points[i].changed.emit({
                name: debugName,
            });
        }

        // points are emitted first so FrmRefPoint links reposition before any
        // layout listener reads bounds; the thing-level signal then tells the
        // layout engine this shape's bounding box may have changed.
        this.thing.changed.emit(debugName);
    }

    movePoint(point: FrmShapePoint, dx: number, dy: number) {
        const start = this.indexOfPoint(point);

        for(let i = start; i < this.points.length; i++) {
            const point = this.points[i];
            point.x += dx;
            point.y += dy;
        }
    }

    lastUpdateLengthId = 0;

    updateLength(updateLengthId: number, pointA: FrmShapePoint, pointB: FrmShapePoint, value: number, debugStr: string = "(none)", editingDim?: FrmShapeDimension) {
        if(!this.isDone) {
            throw new Error("updateLength() must be called after done()")
        }

        if(updateLengthId === this.lastUpdateLengthId) {
            // console.log("skipping update", debugStr, "value", value);
            return;
        }

        this.lastUpdateLengthId = updateLengthId;

        if(DEBUG) console.log(this.name, "start", debugStr, "value", value);

        this.lines.forEach(l => {
            l.kiwiVar = null;
        })

        const currentDist = distance(pointA, pointB);
        const dirX = pointB.x - pointA.x;
        const dirY = pointB.y - pointA.y;

        const uvX = dirX / currentDist;
        const uvY = dirY / currentDist;

        const sol = new kiwi.Solver();

        const matchLine = this.lines.find(l => {
            if(l.pointA === pointA && l.pointB === pointB) return true;
            if(l.pointA === pointB && l.pointB === pointA) return true;
            return false;
        });

        let lineIsFlipped = new Set<FrmShapeLine>();

        this.lines.forEach(l => {
            if(!l.isParallel(uvX, uvY)) {
                return
            }

            l.kiwiVar = new kiwi.Variable("line:" + l.id);
            sol.addEditVariable(l.kiwiVar, kiwi.Strength.medium);

            let suggest = 0;
            let constraints: string[] = [];

            try {
                if (l.isFlex) {
                    if (matchLine === l) {
                        suggest = value;
                    } else {
                        let length = l.length;

                        if (!l.directionMatches(uvX, uvY)) {
                            lineIsFlipped.add(l);
                            length = -length;
                        }

                        suggest = length;
                    }

                    if (suggest > 0) {
                        // not-zero requirement
                        constraints.push(`rq($ >= ${MIN_FLEX_LENGTH})`)
                        const con = new kiwi.Constraint(l.kiwiVar, kiwi.Operator.Ge, MIN_FLEX_LENGTH, kiwi.Strength.required);
                        sol.addConstraint(con);

                        // nice
                        constraints.push(`plz($ >= ${suggest / 4})`)
                        const con2 = new kiwi.Constraint(l.kiwiVar, kiwi.Operator.Ge, suggest / 4, kiwi.Strength.medium);
                        sol.addConstraint(con2);
                    } else {
                        // not-zero requirement
                        constraints.push(`rq($ <= ${-MIN_FLEX_LENGTH})`)
                        const con = new kiwi.Constraint(l.kiwiVar, kiwi.Operator.Le, -MIN_FLEX_LENGTH, kiwi.Strength.required);
                        sol.addConstraint(con);

                        // nice
                        constraints.push(`plz($ <= ${suggest / 4})`)
                        const con2 = new kiwi.Constraint(l.kiwiVar, kiwi.Operator.Le, suggest / 4, kiwi.Strength.medium);
                        sol.addConstraint(con2);
                    }
                } else {
                    // not flex

                    const length = l.length;

                    suggest = length;
                    constraints.push(`rq($ <= ${length} - fixed)`)
                    const con = new kiwi.Constraint(l.kiwiVar, kiwi.Operator.Eq, length, kiwi.Strength.required);
                    sol.addConstraint(con);
                }

                sol.suggestValue(l.kiwiVar, suggest);
            } finally {
                if (DEBUG) console.log("suggest: line", l.debugLabelText, suggest, constraints.join(" "));
            }
        });

        this.constraints.forEach(c => {
            if(c.lines.every(l => l.kiwiVar !== null)) {
                const lineA = c.lines[0];
                const lineB = c.lines[1];

                const a = lineA.kiwiVar!;
                const b = lineB.kiwiVar!;

                if(lineIsFlipped.has(lineA) !== lineIsFlipped.has(lineB)) {
                    if(DEBUG) console.log("constraint", c.lines[0].debugLabelText + " = -" + c.lines[1].debugLabelText)
                    const con = new kiwi.Constraint(new kiwi.Expression([-1, a]), kiwi.Operator.Eq, b, kiwi.Strength.required)
                    sol.addConstraint(con);
                    return
                }

                if(DEBUG) console.log("constraint", c.lines[0].debugLabelText + " = " + c.lines[1].debugLabelText)
                const con = new kiwi.Constraint(a, kiwi.Operator.Eq, b, kiwi.Strength.required);
                sol.addConstraint(con);
            }
        });

        let afterFxs: (() => void)[] = [];
        let unsatisfiable = false;

        this.dimensions.forEach(c => {
            let matches = false;

            if(c.pointA === pointA && c.pointB === pointB) {
                matches = true;
            } else if(c.pointB === pointA && c.pointA === pointB) {
                matches = true;
            }

            if (!c.userDefined) return;
            if (!c.isParallel(uvX, uvY)) return;

            let lengthValue = 0;
            if(matches) {
                lengthValue = value;
            } else {
                lengthValue = c.userDefined ? c.expectedLength : c.length;
            }

            const aBeforeB = this.points.indexOf(c.pointA) < this.points.indexOf(c.pointB);

            let lines: FrmShapeLine[] = [];

            if(aBeforeB) {
                lines = this.linesBetween(c.pointA, c.pointB)
                    .filter(l => l.kiwiVar !== null)
            } else {
                lines = this.linesBetween(c.pointB, c.pointA)
                    .filter(l => l.kiwiVar !== null)
            }

            let items: any[] = [];
            let debugStr: string[] = [];

            for(let j = 0; j < lines.length; j++) {
                items.push(lines[j].kiwiVar!);
                debugStr.push(lines[j].debugLabelText);
            }

            if(lines.length === 0) {
                console.warn("no lines");
            }


            if(c.isReverseDirection(uvX, uvY)) {
                lengthValue = -lengthValue;
            }

            const exp = new kiwi.Expression(...items);
            const con = new kiwi.Constraint(exp, kiwi.Operator.Eq, lengthValue, kiwi.Strength.required);

            if(DEBUG) console.log(debugStr.join("+") + " = " + lengthValue, c.frmVar.getDebug());

            // a dimension that cannot fit (e.g. a jamb depth shorter than the rabbets it
            // must contain) leaves the shape as-is rather than taking the tool down
            try {
                sol.addConstraint(con);
            } catch (e) {
                console.warn("unsatisfiable dimension", this.name, debugStr.join("+") + " = " + lengthValue, c.frmVar.getDebug(), e);
                unsatisfiable = true;
            }

            if(matches) {
                afterFxs.push(() => {
                    const length = lines.map(l => l.length).reduce((a, b) => a + b);
                    c.frmVar.set(length, {
                        debug: "shape-update:after",
                        shapeUpdateId: updateLengthId,
                    });
                });
            }
        });

        if(unsatisfiable) return;

        try {
            if(DEBUG) console.log("updateVariables");
            sol.updateVariables();
        } catch (e) {
            console.warn("updateVariables failed", e);
            return;
        }

        this.lines.forEach(l => {
            if (l.kiwiVar === null) return;
            if(DEBUG) console.log("got", l.debugLabelText, l.kiwiVar.value(), "current=" + l.length, lineIsFlipped.has(l) ? "(flipped)" : "" );
        });

        this.lines.forEach(l => {
            if(l.kiwiVar === null) return;

            let value = l.kiwiVar.value();
            if(!l.isFlex) {
                if(Math.abs(value - l.length) > EPS) throw new Error(`fixed-length issue on ${l.debugLabelText} ${value} != ${l.length}`);
            }

            let adjust = Math.abs(value) - l.length;
            if(Math.abs(adjust) < EPS) return;

            if(DEBUG) console.log("adjust", l.debugLabelText, adjust);

            // flip for uv*
            if(lineIsFlipped.has(l)) {
                adjust = -adjust;
            }

            const adjustX = adjust * uvX;
            const adjustY = adjust * uvY;

            this.movePoint(l.pointB, adjustX, adjustY)
        })

        afterFxs.forEach(f => f());
        this.draw(updateLengthId);
        this.emitChanges("Shape.updateLength");
    }

    indexOfPoint(point: FrmShapePoint) {
        return this.points.indexOf(point);
    }

    private linesBetween(pointA: FrmShapePoint, pointB: FrmShapePoint) {
        const start = this.indexOfPoint(pointA);
        const end = this.indexOfPoint(pointB);

        const lines: FrmShapeLine[] = [];
        for (let i = start; i < end; i++) {
            lines.push(this.lines[i]);
        }

        return lines;
    }

    static updateLengthIdCounter = 1;

    static getUpdateLengthId() {
        if(this.updateLengthIdCounter > 5000000) {
            this.updateLengthIdCounter = 1;
        }

        return this.updateLengthIdCounter++;
    }
}

